import React, { createContext, useContext, useEffect, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';
import { useLoyaltyServiceAppliedOfferRecovery } from 'features/offer/hooks/use-loyalty-service-applied-offer-recovery';

import { IBaseProps, SetState } from '@rbi-ctg/frontend';
import { IQuest } from 'components/quests/loyalty-quest/types';
import { useLoyaltyQuestQuery } from 'components/quests/loyalty-quest/use-loyalty-quest-query';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useEffectOnce from 'hooks/use-effect-once';
import { usePrevious } from 'hooks/use-previous';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import {
  IUseLoyaltyCooldown,
  IUseLoyaltyOffers,
  IUseLoyaltyRewards,
  IUseLoyaltyUserNotifications,
} from './hooks/types';
import { useGetOffersIncentivesIds } from './hooks/use-get-offers-incentives';
import { useIsLoyaltyEnabled } from './hooks/use-is-loyalty-enabled';
import { useLoyaltyCooldown } from './hooks/use-loyalty-cooldown';
import { useLoyaltyOffers } from './hooks/use-loyalty-offers';
import { useLoyaltyRewards } from './hooks/use-loyalty-rewards';
import { useLoyaltyUserNotifications } from './hooks/use-loyalty-user-notifications';
import { useLoyaltyUserState } from './hooks/use-loyalty-user-state';
import { InRestaurantRedemptionProvider } from './in-restaurant-redemption';

// Leaving as an interface to be extended in future PR
export interface ILoyaltyContext
  extends IUseLoyaltyRewards,
    IUseLoyaltyOffers,
    IUseLoyaltyCooldown,
    IUseLoyaltyUserNotifications {
  claimPointsEnabled: boolean;
  refetchLoyaltyUser: () => void;
  addOffersCallbackUrl: string;
  setAddOffersCallbackUrl: SetState<string>;
  loadingQuests: boolean;
  userQuest: IQuest | null;
  refetchQuests: () => void;
  loyaltyUserId: string | undefined;
  loyaltyError: ApolloError | undefined;
}

export const LoyaltyContext = createContext({} as ILoyaltyContext);
export const useLoyaltyContext = () => useContext(LoyaltyContext);

export const LoyaltyProvider = (props: IBaseProps) => {
  const claimPointsEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_CLAIM_LOYALTY_POINTS));
  const { enabled: enableLoyaltyServiceOffers } = useEnableLoyaltyServiceOffers();
  const [addOffersCallbackUrl, setAddOffersCallbackUrl] = useState<string>('');
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const {
    loading: loadingLoyaltyUser,
    loyaltyUser,
    refetch: refetchLoyaltyUser,
    error: loyaltyError,
  } = useLoyaltyUserState({ refreshOnMountOrFocus: false });
  const loyaltyUserId = loyaltyUser?.id;
  const prevLoyaltyUserId = usePrevious(loyaltyUserId);
  const loyaltyUserPoints = loyaltyUser?.points ?? 0;

  useLoyaltyServiceAppliedOfferRecovery({
    enabled: enableLoyaltyServiceOffers,
    loadingLoyaltyUser,
    loyaltyUserId,
  });

  const loyaltyRewards = useLoyaltyRewards(loyaltyUser);
  const loyaltyOffers = useLoyaltyOffers({
    legacyOfferQueriesEnabled: !enableLoyaltyServiceOffers,
  });
  const loyaltyCooldown = useLoyaltyCooldown(loyaltyUser, refetchLoyaltyUser);
  const loyaltyNotifications = useLoyaltyUserNotifications({ loyaltyId: loyaltyUserId });
  const { fetchIncentiveIds } = useGetOffersIncentivesIds();
  const dispatch = useAppDispatch();
  const shouldRefetch = useAppSelector(selectors.loyalty.selectShouldRefetchRewards);
  // Fetch the Quest stored in our Loyalty service for the current User
  const { loadingQuests, quest: userQuest, refetchQuests } = useLoyaltyQuestQuery({
    loyaltyId: loyaltyUserId,
  });

  useEffectOnce(() => {
    if (loyaltyEnabled) {
      fetchIncentiveIds().then(ids => dispatch(actions.loyalty.setIncentivesIds(ids || [])));
    }
  });

  useEffectOnUpdates(() => {
    if (!loyaltyEnabled) {
      dispatch(actions.loyalty.resetLoyaltyRewardsState({ points: loyaltyUserPoints }));
    }
  }, [dispatch, loyaltyEnabled, loyaltyUserPoints]);

  const { refetchRewards, engineRewardsMap } = loyaltyRewards;
  // Reset rewards on loyalty user change
  useEffect(() => {
    if (prevLoyaltyUserId && !loadingLoyaltyUser && prevLoyaltyUserId !== loyaltyUserId) {
      dispatch(actions.loyalty.resetLoyaltyRewardsState({ points: loyaltyUserPoints }));
    }
  }, [
    dispatch,
    loadingLoyaltyUser,
    loyaltyUserId,
    loyaltyUserPoints,
    prevLoyaltyUserId,
    shouldRefetch,
  ]);

  useEffect(() => {
    if (shouldRefetch && loyaltyUserId) {
      refetchRewards(loyaltyUserId);
      dispatch(actions.loyalty.setShouldRefetchRewards(false));
    }
  }, [dispatch, loyaltyUserId, refetchRewards, shouldRefetch]);

  useEffect(() => {
    const engineRewardsList = Object.values(engineRewardsMap);
    if (engineRewardsList?.length) {
      dispatch(actions.loyalty.createAvailableLoyaltyRewardsMap(engineRewardsList));
    }
  }, [dispatch, engineRewardsMap]);

  const value = {
    loyaltyUserId,
    loyaltyError,
    addOffersCallbackUrl,
    claimPointsEnabled,
    refetchLoyaltyUser,
    setAddOffersCallbackUrl,
    loadingQuests,
    userQuest,
    refetchQuests,
    // other loyalty hooks
    ...loyaltyOffers,
    ...loyaltyRewards,
    ...loyaltyCooldown,
    ...loyaltyNotifications,
  };

  return (
    <LoyaltyContext.Provider value={value}>
      <InRestaurantRedemptionProvider>{props.children}</InRestaurantRedemptionProvider>
    </LoyaltyContext.Provider>
  );
};
